import React, { useEffect, useState } from "react";

import { Catalogos, DetalleLista, ListaResumen, Miembro, ResultadoSync, correoApi, errorTexto, fechaHora } from "./correo.service";
import "../avisos/avisos.css";
import "./correo.css";

const ESTADO_MIEMBRO: Record<string, string> = { entra: "Recibe", sin_buzon: "Sin Buzón", externo: "Otro Dominio", excluido: "No Entra" };

/**
 * Listas de Correo (Super Admin; Esteban 2026-10-10): listas de distribución por rol que WAPP calcula y sincroniza con un
 * alias de Migadu. Reglas (rol + alcance), altas/exclusiones a mano, miembros con el motivo de cada uno y avisos.
 */
const ListasCorreo: React.FC = () => {
	const [d, setD] = useState<{ listas: ListaResumen[]; catalogos: Catalogos; automatico: boolean } | null>(null);
	const [sel, setSel] = useState<number | null>(null);
	const [nueva, setNueva] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [ok, setOk] = useState<string | null>(null);
	const [ocupado, setOcupado] = useState(false);

	const cargar = () => correoApi.listas().then((r) => setD(r.data)).catch((e) => setError(errorTexto(e)));
	useEffect(() => { cargar(); }, []);

	const todas = async (simular: boolean) => {
		setOcupado(true); setError(null); setOk(null);
		try {
			const r = (await correoApi.sincronizar(null, simular)).data.resultado;
			const xs = Object.values(r);
			const fallas = xs.filter((x) => x.error).length;
			const cambios = xs.reduce((s, x) => s + (x.entran?.length || 0) + (x.salen?.length || 0), 0);
			setOk((simular ? "Vista previa: " : "Sincronizado: ") + cambios + " cambios en " + xs.length + " listas" + (fallas ? ", " + fallas + " con problema" : "") + ".");
			await cargar();
		} catch (e) { setError(errorTexto(e)); } finally { setOcupado(false); }
	};

	if (!d) return <div className="avisos-pag">{error ? <div className="av-aviso oro">{error}</div> : <div className="loader" />}</div>;
	return (
		<div className="avisos-pag co">
			<div className="av-cab">
				<h1>Listas de Correo</h1>
				<div className="av-acciones">
					<button className="av-btn" disabled={ocupado} onClick={() => todas(true)}>Ver Cambios</button>
					<button className="av-btn" disabled={ocupado} onClick={() => todas(false)}>{ocupado ? "Sincronizando…" : "Sincronizar Todas"}</button>
					<button className="av-btn pri" onClick={() => { setNueva(true); setSel(null); }}>Nueva Lista</button>
				</div>
			</div>
			{!d.automatico ? <div className="av-tenue co-nota">Los cambios quedan en WAPP y llegan a Migadu con "Sincronizar" (se enciende solo y cada noche en la mudanza).</div> : null}
			{error ? <div className="av-aviso oro" onClick={() => setError(null)}>{error}</div> : null}
			{ok ? <div className="av-aviso ok" onClick={() => setOk(null)}>{ok}</div> : null}
			<div className={"avk" + (sel || nueva ? " con-detalle" : "")}>
				<div className="avk-lista">
					{d.listas.map((l) => (
						<button key={l.id} className={"avk-tipo" + (sel === l.id ? " on" : "") + (!l.activa ? " apagado" : "")} onClick={() => { setSel(l.id); setNueva(false); }}>
							<span className="avk-tipo-nom"><b>{l.nombre}</b><small>{l.email.split("@")[0]} · {l.reglas ? l.reglas + (l.reglas === 1 ? " regla" : " reglas") + " · " : ""}{l.manuales} a mano</small></span>
							<span className="avk-tipo-cifras">
								{l.avisos ? <em className="oro" title="Miembros que no reciben">{l.avisos} sin correo</em> : null}
								<span title="Reciben">{l.entran}</span>
							</span>
						</button>
					))}
				</div>
				{nueva ? <NuevaLista dominio={d.catalogos.dominio} onCerrar={() => setNueva(false)} onCreada={(id) => { setNueva(false); setSel(id); cargar(); }} />
					: sel ? <DetalleListaPanel key={sel} id={sel} cat={d.catalogos} onCerrar={() => setSel(null)} onCambio={cargar} />
						: <div className="avk-detalle avk-nada">Elige una lista para ver sus reglas y quién la recibe.</div>}
			</div>
		</div>
	);
};

const NuevaLista: React.FC<{ dominio: string; onCerrar: () => void; onCreada: (id: number) => void }> = ({ dominio, onCerrar, onCreada }) => {
	const [f, setF] = useState({ local_part: "", nombre: "", descripcion: "" });
	const [error, setError] = useState<string | null>(null);
	const crear = async () => {
		setError(null);
		try { onCreada((await correoApi.guardarLista(null, { ...f, activa: true })).data.detalle.lista.id); } catch (e) { setError(errorTexto(e)); }
	};
	return (
		<div className="avk-detalle">
			<div className="avk-det-cab"><h2>Nueva Lista</h2><button className="av-x grande" onClick={onCerrar} aria-label="Cerrar">×</button></div>
			{error ? <div className="av-aviso oro" onClick={() => setError(null)}>{error}</div> : null}
			<div className="avk-form">
				<label>Dirección<span className="co-dir"><input value={f.local_part} onChange={(e) => setF({ ...f, local_part: e.target.value })} placeholder="compras" /><i>@{dominio}</i></span></label>
				<label>Nombre<input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} /></label>
				<label>Descripción<input value={f.descripcion} onChange={(e) => setF({ ...f, descripcion: e.target.value })} /></label>
			</div>
			<button className="av-btn pri" disabled={!f.local_part.trim()} onClick={crear}>Crear Lista</button>
		</div>
	);
};

const DetalleListaPanel: React.FC<{ id: number; cat: Catalogos; onCerrar: () => void; onCambio: () => void }> = ({ id, cat, onCerrar, onCambio }) => {
	const [d, setD] = useState<DetalleLista | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [ok, setOk] = useState<string | null>(null);
	const [ocupado, setOcupado] = useState(false);
	const [sync, setSync] = useState<ResultadoSync | null>(null);
	const [regla, setRegla] = useState<{ role_id: number | ""; alcance: string; campus_ids: number[]; pais: string }>({ role_id: "", alcance: "todos", campus_ids: [], pais: "HN" });
	const [man, setMan] = useState<{ q: string; user_id: number | null; email: string; excluir: boolean }>({ q: "", user_id: null, email: "", excluir: false });
	const [personas, setPersonas] = useState<{ id: number; nombre: string; email: string | null }[]>([]);
	const [verMiembros, setVerMiembros] = useState("todos");
	const [bit, setBit] = useState(false);

	useEffect(() => { correoApi.lista(id).then((r) => setD(r.data)).catch((e) => setError(errorTexto(e))); }, [id]);
	useEffect(() => {
		if (man.user_id || man.q.trim().length < 2 || man.q.includes("@")) { setPersonas([]); return; }
		const t = setTimeout(() => correoApi.personas(man.q).then((r) => setPersonas(r.data.personas)).catch(() => setPersonas([])), 250);
		return () => clearTimeout(t);
	}, [man.q, man.user_id]);

	const hacer = async (fn: () => Promise<any>, msg?: string) => {
		setOcupado(true); setError(null); setOk(null);
		try {
			const r = await fn();
			if (r?.data?.detalle) { setD(r.data.detalle); if (r.data.sync) setSync(r.data.sync); }
			if (msg) setOk(msg);
			onCambio();
		} catch (e) { setError(errorTexto(e)); } finally { setOcupado(false); }
	};
	const sincronizar = async (simular: boolean) => {
		setOcupado(true); setError(null); setOk(null);
		try {
			const r = Object.values((await correoApi.sincronizar(id, simular)).data.resultado)[0];
			if (r?.error) setError(r.error); else { setSync(r); setOk(simular ? "Vista previa: no se cambió nada." : "Lista sincronizada con Migadu."); }
			if (!simular) { setD((await correoApi.lista(id)).data); onCambio(); }
		} catch (e) { setError(errorTexto(e)); } finally { setOcupado(false); }
	};

	if (!d) return <div className="avk-detalle">{error ? <div className="av-aviso oro">{error}</div> : <div className="loader" />}</div>;
	const l = d.lista;
	const miembros = d.miembros.filter((m) => verMiembros === "todos" || m.estado === verMiembros);
	const conteo = (e: string) => d.miembros.filter((m) => m.estado === e).length;
	const problemas = d.miembros.filter((m) => m.estado === "sin_buzon" || (m.estado === "externo" && l.externos !== "reenviar"));

	return (
		<div className="avk-detalle">
			<div className="avk-det-cab">
				<div><h2>{l.nombre}</h2><small>{l.email}{l.origen ? " · origen " + l.origen : ""}</small></div>
				<button className="av-x grande" onClick={onCerrar} aria-label="Cerrar">×</button>
			</div>
			{l.descripcion ? <p className="avk-desc">{l.descripcion}</p> : null}
			{error ? <div className="av-aviso oro" onClick={() => setError(null)}>{error}</div> : null}
			{ok ? <div className="av-aviso ok" onClick={() => setOk(null)}>{ok}</div> : null}

			<div className="av-acciones co-top">
				<label className="av-check"><input type="checkbox" checked={l.activa} disabled={ocupado}
					onChange={(e) => hacer(() => correoApi.guardarLista(id, { ...l, activa: e.target.checked }))} /> Lista Activa</label>
				<label className="av-check" title="Quienes tienen su correo en otro dominio reciben la lista por un buzón de reenvío">
					<input type="checkbox" checked={l.externos === "reenviar"} disabled={ocupado}
						onChange={(e) => hacer(() => correoApi.guardarLista(id, { ...l, externos: e.target.checked ? "reenviar" : "omitir" }))} /> Reenviar a Otros Dominios</label>
				<button className="av-btn chico" disabled={ocupado} onClick={() => sincronizar(true)}>Ver Cambios</button>
				<button className="av-btn chico pri" disabled={ocupado} onClick={() => sincronizar(false)}>Sincronizar Ahora</button>
			</div>
			<div className="av-tenue">Última sincronización: {fechaHora(l.sync_at)}{l.sync_detalle && l.sync_detalle.startsWith("Error") ? " · " + l.sync_detalle : ""}</div>

			{problemas.length || d.destinos.omitidos.length ? (
				<div className="av-aviso oro co-sinclic">
					<b>Avisos</b>
					<ul className="co-avisos">
						{problemas.map((m, i) => <li key={i}>{(m.nombre || m.email || "—") + ": " + (m.motivo || "")}{m.estado === "externo" && m.email ? " (" + m.email + ")" : ""}</li>)}
						{d.destinos.omitidos.map((o, i) => <li key={"o" + i}>{o}</li>)}
					</ul>
				</div>
			) : null}

			{sync ? (
				<div className="co-sync">
					<span className="av-etq">Resultado</span>
					<div>{sync.despues.length} destinos en Migadu{sync.entran.length ? " · entran: " + sync.entran.join(", ") : ""}{sync.salen.length ? " · salen: " + sync.salen.join(", ") : ""}
						{!sync.entran.length && !sync.salen.length ? " · sin cambios" : ""}</div>
					{sync.avisos.map((a, i) => <div key={i} className="co-oro">{a}</div>)}
				</div>
			) : null}

			<div className="avk-bloque">
				<div className="avk-bloque-cab"><h3>Reglas por Rol</h3></div>
				<ul className="avk-reglas">
					{d.reglas.length === 0 ? <li className="av-tenue">Sin reglas: solo entran las personas a mano.</li> : d.reglas.map((r) => (
						<li key={r.id}>
							<span className="avk-regla-txt"><b>{r.rol}</b><small>{r.alcance === "todos" ? "En todos los planteles" : r.alcance === "pais" ? "En " + (cat.paises[r.pais || ""] || r.pais) : "En " + r.planteles.join(", ")}</small></span>
							<button className="av-x" disabled={ocupado} aria-label="Quitar regla" onClick={() => hacer(() => correoApi.quitarRegla(r.id))}>×</button>
						</li>
					))}
				</ul>
				<div className="avk-form co-form">
					<label>Rol<select value={regla.role_id} onChange={(e) => setRegla({ ...regla, role_id: e.target.value ? Number(e.target.value) : "" })}>
						<option value="">Elige un rol…</option>{cat.roles.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}</select></label>
					<label>Alcance<select value={regla.alcance} onChange={(e) => setRegla({ ...regla, alcance: e.target.value })}>
						<option value="todos">Todos los Planteles</option><option value="pais">Un País</option><option value="plantel">Planteles</option></select></label>
					{regla.alcance === "pais" ? <label>País<select value={regla.pais} onChange={(e) => setRegla({ ...regla, pais: e.target.value })}>
						{Object.entries(cat.paises).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label> : null}
					{regla.alcance === "plantel" ? (
						<div className="co-planteles">{cat.planteles.map((p) => (
							<label key={p.id} className="av-check"><input type="checkbox" checked={regla.campus_ids.includes(p.id)}
								onChange={(e) => setRegla({ ...regla, campus_ids: e.target.checked ? [...regla.campus_ids, p.id] : regla.campus_ids.filter((x) => x !== p.id) })} /> {p.nombre}</label>
						))}</div>
					) : null}
					<button className="av-btn pri" disabled={ocupado || !regla.role_id} onClick={() => hacer(() => correoApi.agregarRegla(id, regla), "Regla agregada.")}>Agregar Regla</button>
				</div>
			</div>

			<div className="avk-bloque">
				<div className="avk-bloque-cab"><h3>A Mano</h3></div>
				<ul className="avk-reglas">
					{d.manual.length === 0 ? <li className="av-tenue">Nadie a mano.</li> : d.manual.map((m) => (
						<li key={m.id} className={m.excluir ? "excluir" : ""}>
							<span className="avk-regla-txt"><b>{m.nombre || m.email}</b><small>{m.excluir ? "Excluida de la lista" : m.user_id ? "Persona" : "Dirección suelta"}{m.nombre && m.email ? " · " + m.email : ""}</small></span>
							<button className="av-x" disabled={ocupado} aria-label="Quitar" onClick={() => hacer(() => correoApi.quitarManual(m.id))}>×</button>
						</li>
					))}
				</ul>
				<div className="avk-form co-form">
					<label>Persona o dirección
						<input value={man.q} placeholder="Nombre, o un correo completo" onChange={(e) => setMan({ ...man, q: e.target.value, user_id: null, email: e.target.value.includes("@") ? e.target.value.trim() : "" })} />
					</label>
					<label className="av-check"><input type="checkbox" checked={man.excluir} onChange={(e) => setMan({ ...man, excluir: e.target.checked })} /> Excluir en vez de agregar</label>
					<button className="av-btn pri" disabled={ocupado || (!man.user_id && !man.email)}
						onClick={() => hacer(async () => { const r = await correoApi.agregarManual(id, { user_id: man.user_id, email: man.email, excluir: man.excluir }); setMan({ q: "", user_id: null, email: "", excluir: false }); return r; }, man.excluir ? "Excluida." : "Agregada.")}>
						{man.excluir ? "Excluir" : "Agregar"}</button>
				</div>
				{personas.length ? (
					<ul className="co-sug">{personas.map((p) => <li key={p.id}><button className="av-link" onClick={() => { setMan({ ...man, q: p.nombre, user_id: p.id, email: "" }); setPersonas([]); }}>{p.nombre}{p.email ? " · " + p.email : ""}</button></li>)}</ul>
				) : null}
			</div>

			<div className="avk-bloque">
				<div className="avk-bloque-cab">
					<h3>Quién la Recibe ({conteo("entra")})</h3>
					<div className="av-chips co-chips">{[["todos", "Todos", d.miembros.length], ["entra", "Reciben", conteo("entra")], ["sin_buzon", "Sin Buzón", conteo("sin_buzon")], ["externo", "Otro Dominio", conteo("externo")], ["excluido", "No Entran", conteo("excluido")]].map(([k, t, c]) => (
						<button key={k as string} className={verMiembros === k ? "on" : ""} onClick={() => setVerMiembros(k as string)}>{t} <i>{c}</i></button>))}</div>
				</div>
				<ul className="avk-gente co-miembros">
					{miembros.length === 0 ? <li className="av-tenue">Nadie.</li> : miembros.map((m: Miembro, i) => (
						<li key={i}>
							<span className="avk-regla-txt"><b>{m.nombre || m.email}</b><small>{m.email}</small></span>
							<span className="co-razon">{m.razones.join(" · ")}{m.motivo ? <em className={"co-chip " + (m.estado === "entra" ? "" : "oro")}>{m.motivo}</em> : null}</span>
							<em className={"co-chip " + (m.estado === "entra" ? "activo" : "oro")}>{ESTADO_MIEMBRO[m.estado]}</em>
						</li>
					))}
				</ul>
			</div>

			<div className="avk-bloque">
				<button className="av-link" onClick={() => setBit(!bit)}>{bit ? "Ocultar Historial" : "Ver Historial"}</button>
				{bit ? <ul className="co-bit">{d.bitacora.map((x, i) => <li key={i}><b>{x.accion}</b> <span>{x.email || ""} {x.detalle || ""}</span><small>{fechaHora(x.created_at)}{x.actor ? " · " + x.actor : ""}</small></li>)}</ul> : null}
			</div>
			<div className="avk-bloque">
				<button className="av-link" disabled={ocupado} onClick={() => { if (window.confirm("¿Borrar la lista " + l.email + "? También se borra su alias en Migadu.")) hacer(async () => { await correoApi.borrarLista(id); onCerrar(); }); }}>Borrar Lista</button>
			</div>
		</div>
	);
};

export default ListasCorreo;
